import { useState } from "react";
import { useAuth } from "react-oidc-context";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowRightArrowLeft, faUserClock } from "@fortawesome/free-solid-svg-icons";

import replacementsService from "api/replacementsService";
import { applyReplacementSwitch } from "utils/replacementSwitch";
import { ENotificationType, notification } from "utils/notification";
import { Claims } from "dto/User/claims";
import { getUserClaim } from "utils/getUserClaim";

import profileStyle from "./profile.module.scss";

// Zastępstwa w popupie konta (miejsce po przełączniku profili): lista kont, na które
// zalogowany może się dziś przełączyć, oraz powrót na własne konto, gdy pracuje
// w zastępstwie. Renderuje się tylko, gdy jest co pokazać.
const ReplacementsMenu = ({ toggle }: { toggle: () => void }) => {
  const auth = useAuth();
  const { data } = replacementsService.useGetSwitches(!!auth.user);
  const { mutate: switchUser } = replacementsService.useSwitch();
  const [busy, setBusy] = useState<string | null>(null);

  const isSwitched = !!getUserClaim(
    auth.user!,
    Claims.UserIsSwitchedToAnotherUser,
  );
  const switchedToUserId = data?.data?.SwitchedToUserId ?? null;
  const items = data?.data?.Items ?? [];

  if (!isSwitched && items.length === 0) return null;

  const doSwitch = (targetUserId: string | null) => {
    if (busy) return;
    setBusy(targetUserId ?? "self");

    const failed = (text: string) => {
      setBusy(null);
      notification({ type: ENotificationType.ERROR, title: "Zastępstwo", text });
    };

    switchUser(targetUserId, {
      onSuccess: async (resp) => {
        try {
          await applyReplacementSwitch(auth, resp.data);
          toggle();
        } catch (e) {
          // storeUser (brak miejsca / tryb prywatny) albo odrzucony refresh token —
          // popup zostaje, przycisk znów aktywny.
          failed(e instanceof Error ? e.message : "Nie udało się przełączyć konta");
        }
      },
      onError: (error) =>
        failed(
          error?.response?.data?.errorCodes?.[0]?.message ??
            "Nie udało się przełączyć konta",
        ),
    });
  };

  return (
    <div className={profileStyle.profiles}>
      <div className={profileStyle.titleRow}>
        <div className={profileStyle.title}>Zastępstwa</div>
      </div>

      <div className={profileStyle.list}>
        {isSwitched && (
          <div
            className={profileStyle.item}
            onClick={() => doSwitch(null)}
            title="Wróć do pracy na własnym koncie"
          >
            <FontAwesomeIcon icon={faArrowRightArrowLeft} />
            <span className={profileStyle.itemName}>Wróć na swoje konto</span>
          </div>
        )}

        {items.map((item) => {
          const active = item.UserId === switchedToUserId;
          return (
            <div
              key={item.UserId}
              className={`${profileStyle.item} ${active ? profileStyle.active : ""}`}
              onClick={() => !active && doSwitch(item.UserId)}
              title={`Zastępstwo do ${item.EndDate}`}
            >
              <FontAwesomeIcon icon={faUserClock} />
              <span className={profileStyle.itemName}>{item.Label}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default ReplacementsMenu;
